import { useEffect, useState } from "react";
import {
  adminListVouchers,
  adminCreateVoucher,
  adminDeleteVoucher,
  adminRenameVoucher,
} from "@/lib/api";

/**
 * AdminVouchers — internal gift-card / voucher issuance.
 * Single-use codes redeemed once at cart checkout. Manual delivery
 * (owner emails / WhatsApps the code to the customer themselves).
 */
const formatEUR = (n) => `€${Number(n || 0).toFixed(2)}`;
const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const AdminVouchers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("5.00");
  const [note, setNote] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [justCreated, setJustCreated] = useState(null);
  // Inline rename state — { code } is the row currently being edited,
  // `renameValue` its draft input, `renameErr` any server-side error.
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameErr, setRenameErr] = useState(null);
  const [renameBusy, setRenameBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminListVouchers();
      setRows(Array.isArray(list) ? list : []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true); setErr(null); setMsg(null); setJustCreated(null);
    try {
      const payload = {
        amount: Number(amount),
        note: note.trim() || null,
        code: customCode.trim() || null,
      };
      const v = await adminCreateVoucher(payload);
      setJustCreated(v);
      setMsg(`Voucher ${v.code} created — copy the code and send it to your client.`);
      setAmount("5.00"); setNote(""); setCustomCode("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Could not create voucher.");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete voucher ${code}? This cannot be undone.`)) return;
    try {
      await adminDeleteVoucher(code);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Could not delete voucher.");
    }
  };

  const copy = (code) => {
    try { navigator.clipboard?.writeText(code); } catch (_e) { /* silent */ }
  };

  const startRename = (code) => {
    setRenaming(code);
    setRenameValue(code);
    setRenameErr(null);
  };
  const cancelRename = () => {
    setRenaming(null);
    setRenameValue("");
    setRenameErr(null);
  };
  const saveRename = async () => {
    const next = renameValue.trim().toUpperCase();
    if (!next) { setRenameErr("Code required"); return; }
    if (next === renaming) { cancelRename(); return; }
    setRenameBusy(true); setRenameErr(null);
    try {
      await adminRenameVoucher(renaming, next);
      cancelRename();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setRenameErr(typeof detail === "string" ? detail : "Rename failed");
    } finally {
      setRenameBusy(false);
    }
  };

  const activeCount = rows.filter((r) => !r.redeemed).length;
  const redeemedCount = rows.filter((r) => r.redeemed).length;

  return (
    <div className="p-6 md:p-10 space-y-8">
      {/* Create form */}
      <section className="bg-white border border-[#F1DCC5] rounded-2xl p-6" data-testid="admin-voucher-create">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">VOUCHERS</div>
            <h2 className="font-serif-display text-2xl mt-1">Issue a gift card</h2>
            <p className="text-xs text-[#8A7568] mt-1">
              Single-use codes. Redeemed once at cart checkout. Deliver the code manually by email or WhatsApp.
            </p>
          </div>
          <div className="text-right text-xs text-[#8A7568]">
            <div><b className="text-[#241C18]">{activeCount}</b> active</div>
            <div><b className="text-[#241C18]">{redeemedCount}</b> redeemed</div>
          </div>
        </div>

        <div className="grid md:grid-cols-[140px_1fr_180px_auto] gap-3 items-end">
          <label className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">AMOUNT (EUR)</span>
            <input
              data-testid="admin-voucher-amount"
              type="number"
              step="0.5"
              min="0.5"
              max="1000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
            />
          </label>
          <label className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">NOTE (client name/email)</span>
            <input
              data-testid="admin-voucher-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Maria — instagram request"
              className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
            />
          </label>
          <label className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">CUSTOM CODE (optional)</span>
            <input
              data-testid="admin-voucher-code"
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
              placeholder="Auto"
              className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm font-typewriter tracking-[0.05em]"
            />
          </label>
          <button
            data-testid="admin-voucher-create-btn"
            onClick={create}
            disabled={creating || !amount || Number(amount) <= 0}
            className="btn-orange text-xs disabled:opacity-40 whitespace-nowrap"
          >
            {creating ? "Creating…" : "Create voucher"}
          </button>
        </div>

        {msg && <div className="mt-3 text-sm text-[#4a7a2c]">{msg}</div>}
        {err && <div className="mt-3 text-sm text-[#8B2E14]">{err}</div>}

        {justCreated && (
          <div
            data-testid="admin-voucher-created-card"
            className="mt-4 rounded-lg border-2 border-[var(--gl-orange)] bg-[#FFF8EA] p-4 flex items-center justify-between gap-3"
          >
            <div>
              <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">READY TO SEND</div>
              <div className="font-serif-display text-3xl mt-0.5 tracking-tight">{justCreated.code}</div>
              <div className="text-sm text-[#241C18] mt-1">
                {formatEUR(justCreated.amount)} · single use{justCreated.note ? ` · ${justCreated.note}` : ""}
              </div>
            </div>
            <button
              onClick={() => copy(justCreated.code)}
              className="font-badge text-[11px] tracking-[0.22em] uppercase bg-[#241C18] text-[#F9F8F5] px-4 py-2 rounded"
            >
              Copy code
            </button>
          </div>
        )}
      </section>

      {/* List */}
      <section className="bg-white border border-[#F1DCC5] rounded-2xl overflow-hidden" data-testid="admin-voucher-list">
        <div className="px-6 py-4 border-b border-[#F1DCC5] flex items-center justify-between">
          <h3 className="font-serif-display text-xl">All vouchers ({rows.length})</h3>
          <button onClick={load} className="text-xs underline underline-offset-2 text-[#8A7568] hover:text-[#241C18]">Refresh</button>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#8A7568]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-[#8A7568]">No vouchers yet. Create one above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFF8EA] text-[#8A7568]">
                <tr className="text-left">
                  <th className="px-4 py-3 font-badge text-[10px] tracking-[0.2em] uppercase">Code</th>
                  <th className="px-4 py-3 font-badge text-[10px] tracking-[0.2em] uppercase">Amount</th>
                  <th className="px-4 py-3 font-badge text-[10px] tracking-[0.2em] uppercase">Note</th>
                  <th className="px-4 py-3 font-badge text-[10px] tracking-[0.2em] uppercase">Status</th>
                  <th className="px-4 py-3 font-badge text-[10px] tracking-[0.2em] uppercase">Redeemed by</th>
                  <th className="px-4 py-3 font-badge text-[10px] tracking-[0.2em] uppercase">Created</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1DCC5]">
                {rows.map((r) => (
                  <tr key={r.code} data-testid={`admin-voucher-row-${r.code}`}>
                    <td className="px-4 py-3 font-typewriter tracking-[0.05em] align-top">
                      {renaming === r.code ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1.5 items-center">
                            <input
                              autoFocus
                              data-testid={`admin-voucher-rename-input-${r.code}`}
                              value={renameValue}
                              onChange={(e) => { setRenameValue(e.target.value.toUpperCase()); setRenameErr(null); }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveRename();
                                if (e.key === "Escape") cancelRename();
                              }}
                              className="w-40 px-2 py-1 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-xs font-typewriter tracking-[0.05em]"
                            />
                            <button
                              data-testid={`admin-voucher-rename-save-${r.code}`}
                              onClick={saveRename}
                              disabled={renameBusy}
                              className="text-xs bg-[#241C18] text-[#F9F8F5] px-2.5 py-1 rounded disabled:opacity-40"
                            >
                              {renameBusy ? "…" : "Save"}
                            </button>
                            <button
                              onClick={cancelRename}
                              className="text-xs underline text-[#8A7568]"
                            >
                              Cancel
                            </button>
                          </div>
                          {renameErr && (
                            <div data-testid={`admin-voucher-rename-error-${r.code}`} className="text-[10px] text-[#8B2E14]">{renameErr}</div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button onClick={() => copy(r.code)} title="Copy code" className="hover:text-[var(--gl-orange-hover)]">
                            {r.code}
                          </button>
                          <button
                            data-testid={`admin-voucher-rename-${r.code}`}
                            onClick={() => startRename(r.code)}
                            className="text-[10px] underline text-[#8A7568] hover:text-[#241C18]"
                          >
                            Rename
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">{formatEUR(r.amount)}</td>
                    <td className="px-4 py-3 text-[#6B6862] align-top">{r.note || "—"}</td>
                    <td className="px-4 py-3 align-top">
                      {r.redeemed ? (
                        <span className="inline-block bg-[#E3DFD7] text-[#241C18] font-badge text-[9px] px-2 py-0.5 rounded-full tracking-[0.2em] uppercase">
                          Redeemed
                        </span>
                      ) : (
                        <span className="inline-block bg-[var(--gl-orange)] text-[var(--gl-orange-text)] font-badge text-[9px] px-2 py-0.5 rounded-full tracking-[0.2em] uppercase">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B6862] align-top">
                      {r.redeemed_by_email || "—"}
                      {r.redeemed_order_number && <div className="text-[10px] font-typewriter">{r.redeemed_order_number}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B6862] align-top">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right align-top">
                      {!r.redeemed && (
                        <button
                          data-testid={`admin-voucher-delete-${r.code}`}
                          onClick={() => remove(r.code)}
                          className="text-xs underline text-[#8B2E14] hover:text-[#5c1e0d]"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminVouchers;
